<template>
  <div class="mod-page">
    <div class="mod-header">
      <div>
        <h1 class="mod-title">Métodos de pago SAT</h1>
        <p class="mod-subtitle">Catálogo SAT de métodos de pago para CFDI</p>
      </div>
      <div class="mod-header-actions">
        <button class="mod-btn mod-btn-outline" @click="goBack">
          <i class="bi bi-arrow-left"></i>
          <span>Catálogos SAT</span>
        </button>
        <button class="mod-btn mod-btn-outline" @click="exportExcel">
          <i class="bi bi-file-earmark-excel"></i>
          <span>Exportar</span>
        </button>
        <button v-if="activeTab === 'empresa'" class="mod-btn mod-btn-outline" :disabled="syncing" @click="copyGlobal">
          <span v-if="syncing" class="mod-spinner"></span>
          <i v-else class="bi bi-arrow-repeat"></i>
          <span>{{ syncing ? 'Copiando...' : 'Copiar global' }}</span>
        </button>
        <button class="mod-btn mod-btn-primary" @click="openForm()">
          <i class="bi bi-plus-lg"></i>
          <span>Nuevo método</span>
        </button>
      </div>
    </div>

    <div class="sat-tabs">
      <button class="sat-tab" :class="{ active: activeTab === 'empresa' }" @click="setTab('empresa')">
        Empresa
      </button>
      <button class="sat-tab" :class="{ active: activeTab === 'global' }" @click="setTab('global')">
        Global SAT
      </button>
    </div>

    <div class="mod-filters">
      <div class="mod-search">
        <i class="bi bi-search"></i>
        <input
          v-model="searchText"
          type="text"
          placeholder="Buscar por clave o descripción..."
          @input="applyFilters"
        >
      </div>
      <div class="mod-filter-right">
        <select v-model="statusFilter" class="mod-select" @change="applyFilters">
          <option value="">Todos</option>
          <option value="activos">Activos</option>
          <option value="inactivos">Inactivos</option>
        </select>
        <select v-model="pageSize" class="mod-select" @change="applyFilters">
          <option :value="10">10</option>
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
        <span class="mod-filter-count">{{ totalFiltered }} registro{{ totalFiltered !== 1 ? 's' : '' }}</span>
      </div>
    </div>

    <div class="mod-table-wrap">
      <table class="mod-table metodo-pago-table">
        <thead>
          <tr>
            <th v-if="activeTab === 'empresa'" class="mod-th-sort" @click="sortBy('EmpresaId')">Empresa <i :class="sortIcon('EmpresaId')"></i></th>
            <th class="mod-th-sort" @click="sortBy('CMetodoPago')">Clave <i :class="sortIcon('CMetodoPago')"></i></th>
            <th class="mod-th-sort" @click="sortBy('Descripcion')">Descripción <i :class="sortIcon('Descripcion')"></i></th>
            <th class="mod-th-sort" @click="sortBy('EsActivo')">Activo <i :class="sortIcon('EsActivo')"></i></th>
            <th class="mod-th-actions">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td :colspan="activeTab === 'empresa' ? 5 : 4" class="mod-td-center">
              <div class="mod-loading"><span class="mod-spinner"></span> Cargando...</div>
            </td>
          </tr>
          <tr v-else-if="pagedItems.length === 0">
            <td :colspan="activeTab === 'empresa' ? 5 : 4" class="mod-td-center mod-td-empty">
              <i class="bi bi-inbox"></i>
              <span>{{ searchText ? 'Sin resultados' : 'No hay métodos de pago registrados' }}</span>
            </td>
          </tr>
          <tr v-for="item in pagedItems" :key="rowKey(item)" class="mod-tr">
            <td v-if="activeTab === 'empresa'">{{ item.EmpresaId }}</td>
            <td><span class="mod-badge">{{ item.CMetodoPago }}</span></td>
            <td><strong>{{ item.Descripcion }}</strong></td>
            <td>
              <span :class="item.EsActivo ? 'mod-status-active' : 'mod-status-inactive'">
                {{ item.EsActivo ? 'Sí' : 'No' }}
              </span>
            </td>
            <td class="mod-td-actions">
              <button class="mod-btn-icon" title="Editar" @click="openForm(item)">
                <i class="bi bi-pencil"></i>
              </button>
              <button class="mod-btn-icon mod-btn-danger" title="Eliminar" @click="confirmDelete(item)">
                <i class="bi bi-trash3"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="totalPages > 1" class="mod-pagination">
      <button class="mod-page-btn" :disabled="currentPage === 1" @click="currentPage = 1">
        <i class="bi bi-chevron-double-left"></i>
      </button>
      <button class="mod-page-btn" :disabled="currentPage === 1" @click="currentPage--">
        <i class="bi bi-chevron-left"></i>
      </button>
      <button
        v-for="p in visiblePages"
        :key="p"
        class="mod-page-btn"
        :class="{ 'mod-page-active': p === currentPage }"
        @click="currentPage = p"
      >{{ p }}</button>
      <button class="mod-page-btn" :disabled="currentPage === totalPages" @click="currentPage++">
        <i class="bi bi-chevron-right"></i>
      </button>
      <button class="mod-page-btn" :disabled="currentPage === totalPages" @click="currentPage = totalPages">
        <i class="bi bi-chevron-double-right"></i>
      </button>
      <span class="mod-page-info">Página {{ currentPage }} de {{ totalPages }}</span>
    </div>

    <MetodoPagoForm
      v-if="showForm"
      :item="selectedItem"
      :is-edit="isEdit"
      :mode="activeTab"
      @saved="onSaved"
      @close="showForm = false"
    />
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import metodoPagoService from './metodopago.service';
import MetodoPagoForm from './MetodoPagoForm.vue';
import authService from '@/services/auth';
import { showToast } from '@/shared/utils/toast';

export default {
  name: 'MetodoPagoList',
  components: { MetodoPagoForm },
  data() {
    return {
      items: [],
      filteredItems: [],
      searchText: '',
      statusFilter: '',
      loading: false,
      syncing: false,
      showForm: false,
      selectedItem: null,
      isEdit: false,
      activeTab: 'empresa',
      currentPage: 1,
      pageSize: 25,
      sortField: 'CMetodoPago',
      sortAsc: true
    };
  },
  computed: {
    totalFiltered() { return this.filteredItems.length; },
    totalPages() { return Math.ceil(this.totalFiltered / this.pageSize) || 1; },
    pagedItems() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredItems.slice(start, start + this.pageSize);
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      let start = Math.max(1, current - 2);
      let end = Math.min(total, current + 2);
      if (end - start < 4) {
        if (start === 1) end = Math.min(total, start + 4);
        else start = Math.max(1, end - 4);
      }
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    }
  },
  watch: {
    currentPage(val) {
      if (val > this.totalPages) this.currentPage = this.totalPages;
    }
  },
  created() {
    this.loadItems();
  },
  methods: {
    async loadItems() {
      this.loading = true;
      try {
        if (this.activeTab === 'global') {
          this.items = await metodoPagoService.listarGlobal({});
        } else {
          const empresa = authService.getSelectedEmpresa();
          this.items = await metodoPagoService.listarEmpresa({ EmpresaId: empresa?.EmpresaId || 0 });
        }
        this.applyFilters();
      } catch (e) {
        console.error('Error al cargar métodos de pago:', e);
        this.items = [];
        this.filteredItems = [];
      } finally {
        this.loading = false;
      }
    },
    setTab(tab) {
      if (this.activeTab === tab) return;
      this.activeTab = tab;
      this.searchText = '';
      this.statusFilter = '';
      this.loadItems();
    },
    rowKey(item) {
      return `${item.EmpresaId || 'global'}-${item.CMetodoPago}`;
    },
    applyFilters() {
      let data = [...this.items];
      const q = this.searchText.toLowerCase().trim();

      if (this.statusFilter === 'activos') data = data.filter(i => i.EsActivo);
      if (this.statusFilter === 'inactivos') data = data.filter(i => !i.EsActivo);

      if (q) {
        data = data.filter(i =>
          (i.CMetodoPago || '').toLowerCase().includes(q) ||
          (i.Descripcion || '').toLowerCase().includes(q)
        );
      }

      data.sort((a, b) => {
        let va = a[this.sortField] ?? '';
        let vb = b[this.sortField] ?? '';
        if (typeof va === 'string') va = va.toLowerCase();
        if (typeof vb === 'string') vb = vb.toLowerCase();
        if (va < vb) return this.sortAsc ? -1 : 1;
        if (va > vb) return this.sortAsc ? 1 : -1;
        return 0;
      });

      this.filteredItems = data;
      this.currentPage = 1;
    },
    sortBy(field) {
      if (this.sortField === field) this.sortAsc = !this.sortAsc;
      else { this.sortField = field; this.sortAsc = true; }
      this.applyFilters();
    },
    sortIcon(field) {
      if (this.sortField !== field) return 'bi bi-chevron-expand mod-sort-inactive';
      return this.sortAsc ? 'bi bi-chevron-up' : 'bi bi-chevron-down';
    },
    openForm(item = null) {
      this.isEdit = !!item;
      this.selectedItem = item ? { ...item } : null;
      this.showForm = true;
    },
    onSaved() {
      this.showForm = false;
      this.loadItems();
    },
    async confirmDelete(item) {
      if (!confirm(`¿Eliminar método de pago "${item.Descripcion}"?`)) return;
      try {
        const result = this.activeTab === 'global'
          ? await metodoPagoService.borrarGlobal(item)
          : await metodoPagoService.borrarEmpresa(item);
        if (result.Number >= 0) this.loadItems();
        else alert(result.Message || 'Error al eliminar');
      } catch (e) {
        console.error('Error al eliminar método de pago:', e);
        alert('Error al eliminar');
      }
    },
    exportExcel() {
      const data = this.filteredItems.map(i => ({
        ...(this.activeTab === 'empresa' ? { Empresa: i.EmpresaId } : {}),
        Clave: i.CMetodoPago,
        Descripción: i.Descripcion,
        Activo: i.EsActivo ? 'Sí' : 'No'
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const colWidths = Object.keys(data[0] || {}).map(k => ({
        wch: Math.max(k.length, ...data.map(r => String(r[k] || '').length)) + 2
      }));
      ws['!cols'] = colWidths;
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'MétodosPago');
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([buf], { type: 'application/octet-stream' }), 'sat_metodos_pago.xlsx');
    },
    goBack() {
      this.$router.push('/catalogos/sat');
    },
    async copyGlobal() {
      if (!confirm('¿Copiar el catálogo global a la empresa seleccionada? Se actualizarán los registros existentes.')) return;
      this.syncing = true;
      try {
        const empresa = authService.getSelectedEmpresa();
        const result = await metodoPagoService.copiarGlobalAEmpresa({ EmpresaId: empresa?.EmpresaId || 0 });
        if (result.Number >= 0) {
          showToast(result.Message || 'Catálogo copiado', result.Type || 'success');
          this.loadItems();
        } else {
          showToast(result.Message || 'No fue posible copiar el catálogo', 'error');
        }
      } catch (e) {
        console.error('Error al copiar catálogo global:', e);
        showToast('Error de conexión al copiar el catálogo', 'error');
      } finally {
        this.syncing = false;
      }
    }
  }
};
</script>

<style scoped>
@import "@/modules/_shared/catalog.css";

.metodo-pago-table {
  min-width: 760px;
}

.sat-tabs {
  display: inline-flex;
  gap: 0.25rem;
  padding: 0.2rem;
  margin-bottom: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
}

.sat-tab {
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #4a5568;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.45rem 0.8rem;
}

.sat-tab.active {
  background: var(--t-primary, #1272c4);
  color: #ffffff;
}
</style>
